import { ArrowRight, ArrowUpRight, ChevronDown } from 'lucide-react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/lib/useScrollReveal';
import { getStudioImage } from '@/lib/studioAssets';
import {
  studioContact,
  studioGames,
  studioMeta,
  studioPhilosophy,
  studioStats,
  type StudioGame,
} from '@/lib/studioConfig';

function openExternal(href?: string) {
  if (!href) return;
  window.open(href, '_blank', 'noopener,noreferrer');
}

function GameCard({ game }: { game: StudioGame }) {
  const statusClass = `s-game-card__status s-game-card__status--${game.status}`;
  return (
    <article className="s-game-card s-reveal" style={{ '--game-accent': game.accent } as CSSProperties}>
      <div className="s-game-card__media">
        <img src={game.hero} alt={game.title} loading="lazy" />
      </div>
      <div className="s-game-card__body">
        <span className={statusClass}>
          <span className="s-dot" />
          {game.statusLabel}
        </span>
        <h3 className="s-game-card__title">{game.title}</h3>
        <p className="s-game-card__subtitle">{game.subtitle}</p>
        <p className="s-game-card__desc">{game.description}</p>
        <div className="s-tags">
          {game.tags.map((tag) => (
            <span key={tag} className="s-tag">
              {tag}
            </span>
          ))}
        </div>
        <div className="s-game-card__platforms">
          {game.platforms.map((platform) => (
            <span key={platform}>{platform}</span>
          ))}
        </div>
        <div className="s-game-card__actions">
          <Link className="s-btn s-btn--primary" to={game.href}>
            进入游戏官网
            <ArrowRight size={17} />
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function StudioHome() {
  useScrollReveal();

  return (
    <main className="studio-shell">
      <header className="s-nav">
        <Link className="s-nav__brand" to="/">
          <img src={getStudioImage('logo')} alt="" />
          {studioMeta.name}
        </Link>
        <nav className="s-nav__links">
          <a href="#games">作品</a>
          <a href="#about">关于</a>
          <a href="#contact">联系</a>
        </nav>
        <a className="s-nav__cta" href="#games">
          查看作品
        </a>
      </header>

      <section className="s-hero">
        <div className="s-hero__glow" />
        <div className="s-hero__grid">
          <div className="s-hero__copy">
            <img className="s-hero__logo" src={getStudioImage('logo')} alt="" />
            <p className="s-eyebrow">{studioMeta.nameEn}</p>
            <h1 className="s-hero__title">
              <span>光年切片</span>
              <span>工作室</span>
            </h1>
            <p className="s-hero__tagline">{studioMeta.tagline}</p>
            <p className="s-hero__intro">{studioMeta.intro}</p>
            <div className="s-hero__actions">
              <a className="s-btn s-btn--primary" href="#games">
                探索我们的作品
                <ArrowRight size={17} />
              </a>
              <Link className="s-btn s-btn--ghost" to="/dahuang">
                查看新作预告
                <ArrowUpRight size={17} />
              </Link>
            </div>
          </div>

          <div className="s-hero__showcase" aria-label="工作室作品预览">
            <div className="s-hero__logo-card">
              <img src={getStudioImage('logo')} alt="" />
              <div>
                <span>{studioMeta.nameEn}</span>
                <strong>把生存系统做成有压力的叙事。</strong>
              </div>
            </div>
            <div className="s-hero__works">
              {studioGames.map((game) => (
                <Link key={game.id} className="s-hero-work" to={game.href}>
                  <img src={game.cover} alt="" />
                  <span>{game.statusLabel}</span>
                  <strong>{game.title}</strong>
                </Link>
              ))}
            </div>
          </div>
        </div>
        <a className="s-hero__scroll" href="#games" aria-label="向下滚动">
          <ChevronDown size={22} />
        </a>
      </section>

      <div className="s-stats">
        {studioStats.map((stat) => (
          <div key={stat.label} className="s-stat s-reveal">
            <span className="s-stat__value">{stat.value}</span>
            <span className="s-stat__label">{stat.label}</span>
          </div>
        ))}
      </div>

      <section className="s-section" id="games">
        <div className="s-reveal">
          <p className="s-eyebrow">OUR GAMES</p>
          <h2 className="s-h2">工作室作品</h2>
          <p className="s-lead">
            每一款都围绕「沉浸式生存经营」展开。从雪夜末世的移动房车，到古代饥荒里的科技庄园，进入任意一款的官网了解更多。
          </p>
        </div>
        <div className="s-games" style={{ marginTop: 56 }}>
          {studioGames.map((game) => (
            <GameCard key={game.id} game={game} />
          ))}
        </div>
      </section>

      <section className="s-philosophy" id="about">
        <div className="s-philosophy__grid">
          <div className="s-reveal">
            <p className="s-eyebrow">{studioPhilosophy.eyebrow}</p>
            <h2 className="s-h2">{studioPhilosophy.title}</h2>
          </div>
          <div className="s-philosophy__body s-reveal">
            {studioPhilosophy.paragraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="s-section s-section--center s-contact" id="contact">
        <div className="s-reveal">
          <p className="s-eyebrow">{studioContact.eyebrow}</p>
          <h2 className="s-h2">{studioContact.title}</h2>
          <p className="s-lead">{studioContact.description}</p>
        </div>
        <div className="s-contact__channels">
          {studioContact.channels.map((channel) => (
            <button
              type="button"
              key={channel.label}
              className="s-contact__card s-reveal"
              onClick={() => openExternal(channel.href)}
            >
              <span>{channel.label}</span>
              <strong>
                {channel.value} <ArrowUpRight size={15} style={{ display: 'inline', verticalAlign: '-2px' }} />
              </strong>
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}
